import React, { useState, useEffect } from 'react'

import { useDropzone } from 'react-dropzone'

import { Typography, FormControl, FormHelperText, Stack, Box, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { Button } from '@repo/ui'
import { Image as ImageIcon } from 'lucide-react'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml']

// The "on light background" swatch stands for a FIXED background the logo will be
// placed on -- not the app's own surface -- so it must not follow the app theme.
// `theme.palette.grey` is remapped per mode (theme.ts darkNeutral), which made this
// swatch render dark in dark mode. Literals = the light-mode grey[50]/[200]/[400].
const LIGHT_SWATCH_BG = 'hsla(180, 5%, 96%, 1)'
const LIGHT_SWATCH_BORDER = 'hsla(216, 5%, 82%, 1)'
const LIGHT_SWATCH_ICON = 'hsla(220, 5%, 53%, 1)'

export type LogoValue = { file: File | null; preview_url: string; isUploaded: boolean }

/**
 * Wide / wordmark logo uploader. Unlike AvatarField (which crops to 1:1), this
 * preserves the original aspect ratio and letterboxes the image inside a wide
 * preview swatch. The swatch is light or dark (`dark` prop) so a brand can see
 * the on-light vs on-dark (knockout) variant against the background it's for.
 */
export const WideLogoField = ({
  value,
  onChange,
  disabled,
  dark = false,
  error: externalError,
}: {
  value?: LogoValue
  onChange?: (value: LogoValue) => void
  disabled?: boolean
  dark?: boolean
  error?: string
}) => {
  const theme = useTheme()
  const [previewUrl, setPreviewUrl] = useState<string>(value?.preview_url || '')
  const [error, setError] = useState<string>('')

  useEffect(() => {
    if (value?.file) {
      const reader = new FileReader()
      reader.onloadend = () => setPreviewUrl(reader.result as string)
      reader.readAsDataURL(value.file)
    } else if (value?.preview_url) {
      setPreviewUrl(value.preview_url)
    } else if (!value) {
      setPreviewUrl('')
    }
  }, [value])

  const processFile = (file: File) => {
    setError('')
    if (file.size > MAX_FILE_SIZE) {
      setError('File size must be less than 10MB')
      return
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Only JPEG, PNG, or SVG files are allowed')
      return
    }
    const reader = new FileReader()
    reader.onloadend = () => {
      const preview = reader.result as string
      setPreviewUrl(preview)
      onChange?.({ file, preview_url: preview, isUploaded: false })
    }
    reader.readAsDataURL(file)
  }

  const { getRootProps, getInputProps, open, isDragAccept, isDragReject } = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/svg+xml': ['.svg'],
    },
    noClick: true,
    noKeyboard: true,
    maxSize: MAX_FILE_SIZE,
    maxFiles: 1,
    multiple: false,
    disabled,
    onDrop: (accepted, rejected) => {
      if (accepted.length > 0) processFile(accepted[0])
      if (rejected.length > 0) {
        rejected[0].errors.forEach((err) => {
          if (err.code === 'file-too-large') setError('File size must be less than 10MB')
          if (err.code === 'file-invalid-type') setError('Only JPEG, PNG, or SVG files are allowed')
        })
      }
    },
  })

  const handleRemove = () => {
    setPreviewUrl('')
    setError('')
    onChange?.({ file: null, preview_url: '', isUploaded: false })
  }

  const displayError = externalError || error
  const swatchBg = dark ? '#1b1b1d' : LIGHT_SWATCH_BG
  const swatchBorder = dark ? alpha('#ffffff', 0.15) : LIGHT_SWATCH_BORDER
  const emptyColor = dark ? alpha('#ffffff', 0.45) : LIGHT_SWATCH_ICON

  return (
    <FormControl error={!!displayError} sx={{ width: '100%' }}>
      <Box
        {...getRootProps()}
        sx={{
          borderRadius: 2,
          border: isDragAccept
            ? `2px dashed ${theme.palette.brand[600]}`
            : isDragReject
              ? `2px dashed ${theme.palette.error.main}`
              : '2px dashed transparent',
          transition: 'border-color 0.2s',
        }}
      >
        <input {...getInputProps()} />
        <Stack spacing={3} direction="row" alignItems="center">
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              height: 64,
              borderRadius: '8px',
              backgroundColor: swatchBg,
              border: `1px solid ${swatchBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              px: 2,
            }}
          >
            {previewUrl ? (
              <Box
                component="img"
                src={previewUrl}
                alt=""
                sx={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
              />
            ) : (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: emptyColor }}>
                <ImageIcon strokeWidth={1} size={18} />
                <Typography variant="body4" sx={{ color: emptyColor }}>
                  {dark ? 'White on dark' : 'Dark on light'}
                </Typography>
              </Stack>
            )}
          </Box>
          <Button variant="tertiary" onClick={open} disabled={disabled}>
            {previewUrl ? 'Change' : 'Upload'}
          </Button>
          {previewUrl && (
            <Button
              variant="text"
              onClick={handleRemove}
              disabled={disabled}
              sx={{ color: theme.palette.text.secondary }}
            >
              Remove
            </Button>
          )}
        </Stack>
      </Box>
      {displayError && <FormHelperText>{displayError}</FormHelperText>}
    </FormControl>
  )
}
